import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTrophy, FaStar, FaCrown, FaFire, FaCalendarWeek,
  FaTicketAlt, FaArrowRight, FaLayerGroup,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.dreamtoday.in";

/* ═══════════════════════════════════════════════════
   PALETTE
═══════════════════════════════════════════════════ */
const G = {
  gold: "#C9A84C",
  goldDeep: "#A8863A",
  goldLight: "#E8C96A",
  goldGlow: "rgba(201,168,76,0.30)",
  cream: "#FFFDF5",
  creamDeep: "#FFF8E7",
  creamMid: "#FFF1CC",
  creamBorder: "#F0D98C",
  text: "#5C4A1E",
  textMid: "#8A6E34",
  textLight: "#B89A5A",
};

/* ═══════════════════════════════════════════════════
   DISPLAY ORDER  — always Daily → Weekly → Monthly
═══════════════════════════════════════════════════ */
const DISPLAY_ORDER = ["daily", "weekly", "monthly"];

const CATEGORY_META = {
  daily: {
    Icon: FaFire,
    badge: "🔥 DAILY",
    label: "Daily",
    sub: "Fresh campaigns every single day",
    iconBg: "linear-gradient(135deg,#F0D060,#C9A84C)",
    shimmer: "rgba(248,220,100,0.20)",
    glow: "rgba(240,200,60,0.50)",
    border: "#E8C96A",
  },
  weekly: {
    Icon: FaCalendarWeek,
    badge: "⚡ WEEKLY",
    label: "Weekly",
    sub: "Bigger prizes every week",
    iconBg: "linear-gradient(135deg,#D4AF37,#A8863A)",
    shimmer: "rgba(212,175,55,0.18)",
    glow: "rgba(201,168,76,0.48)",
    border: "#C9A84C",
  },
  monthly: {
    Icon: FaCrown,
    badge: "👑 MONTHLY",
    label: "Monthly",
    sub: "Grand campaign each month",
    iconBg: "linear-gradient(135deg,#C9A84C,#7A5C1E)",
    shimmer: "rgba(168,134,58,0.18)",
    glow: "rgba(168,134,58,0.48)",
    border: "#A8863A",
  },
};

const getMeta = (name = "") =>
  CATEGORY_META[name.toLowerCase()] || {
    Icon: FaLayerGroup,
    badge: `✦ ${name.toUpperCase()}`,
    label: name.charAt(0).toUpperCase() + name.slice(1),
    sub: "Exclusive Campaigns",
    iconBg: "linear-gradient(135deg,#C9A84C,#7A6020)",
    shimmer: "rgba(201,168,76,0.14)",
    glow: "rgba(201,168,76,0.35)",
    border: G.creamBorder,
  };

/* ═══════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════ */
const SkeletonCard = () => (
  <div
    className="rounded-3xl overflow-hidden animate-pulse"
    style={{
      background: G.creamDeep,
      border: `1.5px solid ${G.creamBorder}`,
      minHeight: 420,
    }}
  >
    <div style={{ height: 250, background: G.creamMid }} />
    <div className="p-5 flex flex-col gap-3">
      <div className="h-5 rounded-lg w-2/3" style={{ background: G.creamMid }} />
      <div className="h-3 rounded-lg w-1/2" style={{ background: G.creamBorder }} />
      <div className="mt-4 h-11 rounded-2xl w-full" style={{ background: G.creamMid }} />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════
   CATEGORY CARD — premium hover
═══════════════════════════════════════════════════ */
const CategoryCard = ({ category, index }) => {
  const navigate = useNavigate();
  const meta = getMeta(category.name);
  const { Icon, badge, label, sub, iconBg, shimmer, glow, border } = meta;
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);
  const imageUrl = category.image ? `${BASE_URL}${category.image}` : null;
  const hasImage = imageUrl && !imgError;

  return (
    <motion.div
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1], duration: 0.3, ease: "easeOut" }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      animate={hovered
        ? { y: -10, boxShadow: `0 24px 64px ${glow}, 0 6px 24px rgba(0,0,0,0.08)` }
        : { y: 0, boxShadow: `0 4px 24px rgba(201,168,76,0.12), 0 1px 6px rgba(0,0,0,0.04)` }
      }
      className="relative rounded-3xl overflow-hidden flex flex-col cursor-pointer"
      style={{
        background: `linear-gradient(165deg,${G.cream} 0%,${G.creamDeep} 60%,${G.creamMid} 100%)`,
        border: `1.5px solid ${hovered ? border : G.creamBorder}`,
        transition: "border-color 0.3s ease",
      }}
      onClick={() => navigate(`/categorycampaigns/${category._id}`)}
    >

      {/* ══ IMAGE ══ */}
      <div className="relative overflow-hidden flex-shrink-0" style={{ height: 250 }}>

        {hasImage ? (
          <motion.img
            src={imageUrl}
            alt={label}
            onError={() => setImgError(true)}
            animate={{ scale: hovered ? 1.09 : 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex flex-col items-center justify-center"
            style={{ background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})` }}
          >
            <Icon style={{ color: G.goldLight, fontSize: 52, opacity: 0.38, marginBottom: 8 }} />
            <span className="text-xs font-medium" style={{ color: G.textLight }}>No Image</span>
          </div>
        )}

        {/* base gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top,rgba(52,32,4,0.90) 0%,rgba(52,32,4,0.22) 46%,transparent 68%)",
          }}
        />

        {/* shimmer sweep */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ x: "-100%", opacity: 0 }}
          animate={hovered ? { x: "150%", opacity: 1 } : { x: "-100%", opacity: 0 }}
          transition={{ duration: 0.65, ease: "easeInOut" }}
          style={{
            background: `linear-gradient(105deg,transparent 30%,${shimmer} 50%,transparent 70%)`,
          }}
        />

        {/* badge */}
        <motion.div
          animate={{ scale: hovered ? 1.06 : 1 }}
          transition={{ duration: 0.22 }}
          className="absolute top-3.5 left-3.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest"
          style={{
            background: "rgba(255,252,235,0.95)",
            color: G.goldDeep,
            border: `1px solid ${G.creamBorder}`,
            backdropFilter: "blur(10px)",
          }}
        >
          {badge}
        </motion.div>

        {/* campaign count */}
        {typeof category.campaignCount === "number" && (
          <div
            className="absolute top-3.5 right-3.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{
              background: "rgba(255,252,235,0.95)",
              border: `1px solid ${G.creamBorder}`,
              backdropFilter: "blur(10px)",
            }}
          >
            <FaTicketAlt style={{ color: G.goldDeep, fontSize: 8 }} />
            <span className="text-[10px] font-bold" style={{ color: G.text }}>
              {category.campaignCount} {category.campaignCount === 1 ? "Campaign" : "Campaigns"}
            </span>
          </div>
        )}

        {/* title */}
        <div className="absolute bottom-0 left-0 right-0 px-5 pb-4">
          <h3
            className="font-extrabold leading-tight"
            style={{
              color: "#FFF8E7",
              textShadow: "0 1px 12px rgba(0,0,0,0.65)",
              fontSize: "clamp(22px,2.6vw,28px)",
            }}
          >
            {label}
          </h3>
          <p className="text-xs mt-0.5 font-medium" style={{ color: "rgba(255,248,231,0.72)" }}>
            {sub}
          </p>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <motion.div
        className="flex items-center justify-between px-5 py-4"
        animate={{
          background: hovered
            ? `linear-gradient(90deg,${shimmer},transparent 70%)`
            : "transparent",
        }}
        transition={{ duration: 0.3 }}
        style={{
          borderTop: `1px solid ${hovered ? border : G.creamBorder}`,
          transition: "border-color 0.3s ease",
        }}
      >
        {/* icon + info */}
        <div className="flex items-center gap-3">
          <motion.div
            animate={hovered
              ? { scale: [1, 1.2, 1.1], rotate: [0, -10, 0] }
              : { scale: 1, rotate: 0 }
            }
            transition={{ duration: 0.42, ease: "easeOut" }}
            className="rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
            style={{ width: 42, height: 42, background: iconBg }}
          >
            <Icon style={{ color: "#fff", fontSize: 17 }} />
          </motion.div>
          <div>
            <p className="text-sm font-extrabold leading-none" style={{ color: G.text }}>
              {label} Campaign
            </p>
            <p className="text-[10px] mt-0.5 font-medium" style={{ color: G.textLight }}>
              {category.campaignCount
                ? `${category.campaignCount} active campaign${category.campaignCount > 1 ? "s" : ""}`
                : "No active campaigns"}
            </p>
          </div>
        </div>

        {/* CTA button */}
        <motion.div
          animate={hovered
            ? {
              background: `linear-gradient(135deg,${G.goldLight},${G.gold})`,
              color: "#3B2A0A",
              borderColor: G.gold,
            }
            : {
              background: G.creamMid,
              color: G.goldDeep,
              borderColor: G.creamBorder,
            }
          }
          transition={{ duration: 0.25 }}
          className="flex items-center gap-1.5 text-xs font-black tracking-wide
            rounded-xl px-3.5 py-2.5"
          style={{ border: "1px solid" }}
        >
          <span>Explore</span>
          <motion.span
            animate={{ x: hovered ? 4 : 0 }}
            transition={{ duration: 0.22 }}
          >
            <FaArrowRight style={{ fontSize: 9 }} />
          </motion.span>
        </motion.div>
      </motion.div>

      {/* ══ GLOW BORDER BLOOM ══ */}
      <motion.div
        className="absolute inset-0 rounded-3xl pointer-events-none"
        animate={hovered
          ? { opacity: 1, boxShadow: `inset 0 0 0 1.5px ${border}` }
          : { opacity: 0, boxShadow: "inset 0 0 0 0px transparent" }
        }
        transition={{ duration: 0.28 }}
      />
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN EXPORT
═══════════════════════════════════════════════════ */
const CampaignCards = () => {
  const [rawCategories, setRawCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/get-categories`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setRawCategories(d.categories || []);
        else throw new Error(d.message || "Failed to load categories");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  /* enforce Daily → Weekly → Monthly, then any extras */
  const categories = [
    ...DISPLAY_ORDER
      .map((key) => rawCategories.find((c) => c.name.toLowerCase() === key))
      .filter(Boolean),
    ...rawCategories.filter((c) => !DISPLAY_ORDER.includes(c.name.toLowerCase())),
  ];

  return (
    <section
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg,${G.cream} 0%,${G.creamDeep} 50%,${G.cream} 100%)`,
        paddingTop: "clamp(48px,8vw,96px)",
        //paddingBottom: "clamp(48px,8vw,96px)",
      }}
    >
      {/* bg orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {[{ top: -160, right: -160 }, { bottom: -160, left: -160 }].map((pos, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              ...pos, width: 500, height: 500,
              background: "radial-gradient(circle,rgba(240,217,140,0.26),transparent 70%)",
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── HEADER ── */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full mb-5"
            style={{
              background: G.creamMid,
              color: G.goldDeep,
              border: `1.5px solid ${G.creamBorder}`,
              boxShadow: `0 2px 14px ${G.goldGlow}`,
              padding: "7px 18px",
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: "0.13em",
              textTransform: "uppercase",
            }}
          >
            <FaStar style={{ color: G.gold, fontSize: 8 }} />
            Campaigns
            <FaStar style={{ color: G.gold, fontSize: 8 }} />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 }}
            className="font-extrabold"
            style={{
              color: G.text,
              fontSize: "clamp(26px,4vw,42px)",
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
            }}
          >
            Choose Your Campaign
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.14 }}
            style={{ color: G.textLight, fontSize: 14, maxWidth: 420, margin: "12px auto 0" }}
          >
            Daily thrills, weekly surprises, or grand monthly Campaigns — pick your campaign.
          </motion.p>

          <div className="flex items-center justify-center gap-2.5 mt-7">
            <div style={{ height: 1, width: "clamp(32px,6vw,96px)", background: `linear-gradient(to right,transparent,${G.creamBorder})` }} />
            <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
            <div style={{ height: 1, width: 20, background: G.creamBorder }} />
            <FaTrophy style={{ color: G.gold, fontSize: 12 }} />
            <div style={{ height: 1, width: 20, background: G.creamBorder }} />
            <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
            <div style={{ height: 1, width: "clamp(32px,6vw,96px)", background: `linear-gradient(to left,transparent,${G.creamBorder})` }} />
          </div>
        </div>

        {/* ── CONTENT ── */}
        <AnimatePresence mode="wait">

          {loading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="grid gap-6 lg:gap-8"
              style={{ gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,280px),1fr))" }}
            >
              {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
            </motion.div>
          )}

          {!loading && error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="text-center rounded-3xl p-12 border"
              style={{ background: "#FFF5F5", borderColor: "#FFCDD2" }}
            >
              <p className="text-lg font-bold mb-1" style={{ color: "#C62828" }}>
                ⚠️ Failed to load categories
              </p>
              <p className="text-sm" style={{ color: "#C62828", opacity: 0.7 }}>{error}</p>
            </motion.div>
          )}

          {!loading && !error && categories.length === 0 && (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="text-center rounded-3xl p-12 border"
              style={{ background: G.creamDeep, borderColor: G.creamBorder }}
            >
              <FaTrophy style={{ color: G.creamBorder, fontSize: 40, margin: "0 auto 12px" }} />
              <p className="text-sm font-medium" style={{ color: G.textLight }}>
                No categories yet — check back soon!
              </p>
            </motion.div>
          )}

          {!loading && !error && categories.length > 0 && (
            <motion.div
              key="cards"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="grid gap-6 lg:gap-8"
              style={{ gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,280px),1fr))" }}
            >
              {categories.map((cat, i) => (
                <CategoryCard key={cat._id} category={cat} index={i} />
              ))}
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </section>
  );
};

export default CampaignCards;